import axios from "axios";

const API_URL =
  import.meta.env.VITE_API_URL || "https://quickdash-app.onrender.com/api";
const FRONTEND_URL = "https://quickdash.dev";

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // DON'T set Content-Type for FormData - let browser handle it
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  return config;
});

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if not already on login page
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/login")) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = `${FRONTEND_URL}/login`;
      }
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (data) => api.post("/auth/register", data),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  refresh: () => api.post("/auth/refresh"),
};

// City API (public - no auth needed)
export const cityAPI = {
  list: (params = {}) => api.get("/cities", { params }),
  get: (id) => api.get(`/cities/${id}`),
  governorates: () => api.get("/cities/governorates"),
  byGovernorate: (governorate) => api.get(`/cities/governorate/${governorate}`),
  distance: (fromCityId, toCityId) =>
    api.get(
      `/cities/distance?from_city_id=${fromCityId}&to_city_id=${toCityId}`,
    ),
};

// Dashboard API (admin only)
export const dashboardAPI = {
  stats: () => api.get("/dashboard/stats"),
  chartData: (days = 30) => api.get(`/dashboard/chart-data?days=${days}`),
  recentActivity: (limit = 10) =>
    api.get(`/dashboard/recent-activity?limit=${limit}`),
  notifications: () => api.get("/dashboard/notifications"),
  markNotificationRead: (id) => api.put(`/dashboard/notifications/${id}/read`),
};

// Orders API
export const ordersAPI = {
  list: (params = {}) => api.get("/orders", { params }),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post("/orders", data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  updateStatus: (id, status, notes = "") =>
    api.put(`/orders/${id}/status`, { status, notes }),
  assignCourier: (id, courierId) =>
    api.post(`/orders/${id}/assign`, { courier_id: courierId }),
  tracking: (id) => api.get(`/orders/${id}/tracking`),
  downloadInvoice: (id) =>
    api.get(`/orders/${id}/invoice/download`, { responseType: "blob" }),
  previewInvoice: (id) =>
    api.get(`/orders/${id}/invoice/preview`, { responseType: "blob" }),
};

// Invoice API (Full Invoice Management System)
export const invoiceAPI = {
  // List all invoices with filters
  list: (params = {}) => api.get("/invoices", { params }),

  // Get invoice stats
  stats: () => api.get("/invoices/stats"),

  // Create manual invoice
  create: (data) => api.post("/invoices", data),

  // Create invoice from order
  createFromOrder: (orderId) => api.post(`/invoices/from-order/${orderId}`),

  // Get single invoice
  get: (id) => api.get(`/invoices/${id}`),

  // Update invoice status
  updateStatus: (id, data) => api.put(`/invoices/${id}/status`, data),

  // Generate PDF
  generatePdf: (id) => api.post(`/invoices/${id}/pdf`),

  // Download PDF
  downloadPdf: (id) =>
    api.get(`/invoices/${id}/download`, { responseType: "blob" }),

  // Delete invoice
  delete: (id) => api.delete(`/invoices/${id}`),

  // Admin invoice routes (same as above but under /admin prefix)
  adminList: (params = {}) => api.get("/admin/invoices", { params }),
  adminStats: () => api.get("/admin/invoices/stats"),
  adminCreate: (data) => api.post("/admin/invoices", data),
  adminCreateFromOrder: (orderId) =>
    api.post(`/admin/invoices/from-order/${orderId}`),
  adminGet: (id) => api.get(`/admin/invoices/${id}`),
  adminUpdateStatus: (id, data) =>
    api.put(`/admin/invoices/${id}/status`, data),
  adminGeneratePdf: (id) => api.post(`/admin/invoices/${id}/pdf`),
  adminDownloadPdf: (id) =>
    api.get(`/admin/invoices/${id}/download`, { responseType: "blob" }),
  adminDelete: (id) => api.delete(`/admin/invoices/${id}`),

  // Customer invoice routes
  customerList: () => api.get("/customer/invoices"),
  customerGet: (id) => api.get(`/customer/invoices/${id}`),
  customerDownload: (id) =>
    api.get(`/customer/invoices/${id}/download`, { responseType: "blob" }),
};

// Admin API
export const adminAPI = {
  users: (params = {}) => api.get("/admin/users", { params }),
  createUser: (data) => api.post("/admin/users", data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  toggleUserStatus: (id) => api.put(`/admin/users/${id}/status`),
  couriers: (params = {}) => api.get("/admin/couriers", { params }),
  courierDetails: (id) => api.get(`/admin/couriers/${id}`),
  approveCourier: (id) => api.put(`/admin/couriers/${id}/approve`),
  customers: (params = {}) => api.get("/admin/customers", { params }),
  customerDetails: (id) => api.get(`/admin/customers/${id}`),
  settings: () => api.get("/admin/settings"),
  updateSettings: (data) => api.put("/admin/settings", data),
  reports: {
    orders: (days = 30) => api.get(`/admin/reports/orders?days=${days}`),
    revenue: (days = 30) => api.get(`/admin/reports/revenue?days=${days}`),
    couriers: (days = 30) => api.get(`/admin/reports/couriers?days=${days}`),
  },
  stats: {
    overview: () => api.get("/admin/stats/overview"),
    revenue: (days = 30) => api.get(`/admin/stats/revenue?days=${days}`),
    orders: (days = 30) => api.get(`/admin/stats/orders?days=${days}`),
    users: () => api.get("/admin/stats/users"),
  },
  // Invoice management (convenience methods using invoiceAPI)
  invoices: (params = {}) => invoiceAPI.adminList(params),
  invoiceStats: () => invoiceAPI.adminStats(),
  createInvoice: (data) => invoiceAPI.adminCreate(data),
  createInvoiceFromOrder: (orderId) => invoiceAPI.adminCreateFromOrder(orderId),
  getInvoice: (id) => invoiceAPI.adminGet(id),
  updateInvoiceStatus: (id, data) => invoiceAPI.adminUpdateStatus(id, data),
  generateInvoicePdf: (id) => invoiceAPI.adminGeneratePdf(id),
  downloadInvoicePdf: (id) => invoiceAPI.adminDownloadPdf(id),
  deleteInvoice: (id) => invoiceAPI.adminDelete(id),
};

// Courier API
export const courierAPI = {
  dashboard: () => api.get("/courier/dashboard"),
  chartData: (days = 7) =>
    api.get(`/courier/dashboard/chart-data?days=${days}`),
  recentActivity: (limit = 5) =>
    api.get(`/courier/dashboard/recent-activity?limit=${limit}`),
  assignedOrders: () => api.get("/courier/assigned-orders"),
  deliveryHistory: (days = 30) =>
    api.get(`/courier/delivery-history?days=${days}`),
  markPickedUp: (id) => api.put(`/courier/orders/${id}/pickup`),
  markDelivered: (id, data) => api.put(`/courier/orders/${id}/deliver`, data),
  markFailed: (id, reason) =>
    api.put(`/courier/orders/${id}/failed`, { reason }),
  updateLocation: (lat, lng) => api.post("/courier/location", { lat, lng }),
  earnings: (days = 30) => api.get(`/courier/earnings?days=${days}`),
  performance: () => api.get("/courier/performance"),
  schedule: () => api.get("/courier/schedule"),
};

// Customer API
export const customerAPI = {
  dashboard: () => api.get("/customer/dashboard"),
  myOrders: (params = {}) => api.get("/customer/my-orders", { params }),
  createOrder: (data) => api.post("/customer/orders", data),
  trackOrder: (id) => api.get(`/customer/orders/${id}/track`),
  cancelOrder: (id) => api.post(`/customer/orders/${id}/cancel`),
  addresses: () => api.get("/customer/addresses"),
  addAddress: (data) => api.post("/customer/addresses", data),
  updateAddress: (id, data) => api.put(`/customer/addresses/${id}`, data),
  deleteAddress: (id) => api.delete(`/customer/addresses/${id}`),
  calculatePrice: (data) => api.post("/customer/calculate-price", data),
  favorites: () => api.get("/customer/favorites"),
  // Customer invoices
  invoices: (params = {}) => api.get("/customer/invoices", { params }),
  getInvoice: (id) => api.get(`/customer/invoices/${id}`),
  downloadInvoice: (id) =>
    api.get(`/customer/invoices/${id}/download`, { responseType: "blob" }),
  // Live tracking
  liveTrack: (id) => api.get(`/customer/orders/${id}/live-track`),
};

// User API
export const userAPI = {
  profile: () => api.get("/user/profile"),
  updateProfile: (data) => api.put("/user/profile", data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append("avatar", file);
    return api.post("/user/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  changePassword: (data) => api.put("/user/password", data),
};

export default api;
